import { Alert, Skeleton, Typography } from 'antd'
import { computeFfsAssetModelValidation } from 'features/libra/stepComponents/ffs/computeFfsAssetModelValidation'
import { FfsAssetModelEditor } from 'features/libra/stepComponents/ffs/FfsAssetModelEditor'
import { useFfsTicketContext } from 'features/libra/stepComponents/ffs/useFfsTicketContext'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { LibraWorflowStepComponentProps } from 'features/libra/workflow/workflowStepsApi'
import { FC, useMemo } from 'react'
import styled from 'styled-components'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

/**
 * Asset model step: embeds `FfsAssetModelEditor` inline.
 *
 * All FFS workspace coupling (selection, coordinate display, point-and-click)
 * lives inside the editor component, which also renders the per-field
 * source/citation pickers inline. This step just gates rendering on ticket data
 * + missing-input messaging.
 */
export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, never>>
> = () => {
  const {
    unit,
    unitId,
    component,
    componentId,
    assetModel,
    assetModelIsSnapshot,
    snapshotCreateFailed,
    isLoading,
    forceRefresh,
  } = useFfsTicketContext()
  const { validation, isComponentInAssetModel } = useMemo(
    () =>
      computeFfsAssetModelValidation({
        unitId,
        componentId,
        assetModel,
      }),
    [unitId, componentId, assetModel]
  )

  useReportValidation(validation)

  if (isLoading) {
    return (
      <Container>
        <Skeleton active />
      </Container>
    )
  }

  if (!unit || !component) {
    return (
      <Container>
        <Alert
          type='error'
          showIcon
          message='Ticket is missing unit or component tag.'
        />
      </Container>
    )
  }

  // TODO: The user should be able to make the asset model in the workflow if it does not exist
  if (!assetModel) {
    return (
      <Container>
        <Typography.Title level={4}>Asset Model</Typography.Title>
        <Alert
          type='warning'
          showIcon
          message='No asset model exists for this unit yet.'
          description='Create one via the unit details page, then return to this workflow.'
        />
      </Container>
    )
  }

  return (
    <Container>
      <Typography.Title level={4}>Asset Model</Typography.Title>
      <Typography.Paragraph type='secondary'>
        {isComponentInAssetModel
          ? 'Edit the component features below. The viewer reflects changes after save.'
          : 'The ticket component is not yet in the asset model -- pick a component type below to add it.'}
      </Typography.Paragraph>
      {assetModelIsSnapshot ? (
        <Alert
          type='info'
          showIcon
          message="Edits apply to this ticket's copy of the asset model."
          description="This workflow works against a snapshot taken when the ticket was first opened. The unit's master asset model is not modified by changes made here."
        />
      ) : snapshotCreateFailed ? (
        <Alert
          type='warning'
          showIcon
          message="Could not create this ticket's asset-model snapshot."
          description='Showing the live asset model instead -- edits here will modify the unit-wide asset model.'
        />
      ) : null}
      <FfsAssetModelEditor
        assetModel={assetModel}
        component={component}
        unit={unit}
        refreshData={forceRefresh}
      />
    </Container>
  )
}
